// paymentClaims — "Customer says paid" on the Order Board (migration 0146, Toni 2026-11-02).
//
// A customer with an e-Transfer order opens the link in their payment email (or the website's order page) and says
// they sent the money: sender name, amount, reference number and a photo / PDF of the bank's confirmation. The card
// moves to the "Customer says paid" column and cannot be cancelled until dispatch decides:
//   • Confirm = the existing Payment Received move (the database confirms the claim in the same write);
//   • Reject  = payment_claim_reject (the card goes back to Pending, or Unassigned when nobody owns it).
// Wolf matches the reference to the e-Transfer inbox (0116) by itself; a match says the money was SENT — dispatch
// still accepts it in the bank. The card row carries the latest claim as `payment_claim` (jsonb, written by the
// database only), so the board sees claims and matches live through the subscription it already has.

import { supabase } from './supabaseClient';
import { authedFetch } from './forwardedSmsApi';
import { refKey } from './paymentClaimLink';

export interface PaymentClaimMatch {
  noticeId: number;
  senderName: string | null;
  amount: number | null;
  interacRef: string | null;
  receivedAt: number | null;
  matchedAt: number | null;
}

export type PaymentClaimStatus = 'pending' | 'confirmed' | 'rejected';

export interface PaymentClaim {
  id: number;
  status: PaymentClaimStatus;
  senderName: string;
  amount: number;
  reference: string;
  submittedAt: number;
  hasProof: boolean;
  proofType: string | null;
  decidedAt: number | null;
  decidedByName: string | null;
  note: string | null;
  match: PaymentClaimMatch | null;
}

const num = (v: unknown): number | null => (v == null || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));

/** payment_board.payment_claim → PaymentClaim, or null for anything that is not one. */
export function claimFromRow(v: any): PaymentClaim | null {
  if (!v || typeof v !== 'object' || num(v.id) == null) return null;
  const status: PaymentClaimStatus = v.status === 'confirmed' || v.status === 'rejected' ? v.status : 'pending';
  const m = v.match && typeof v.match === 'object' && num(v.match.noticeId) != null ? v.match : null;
  return {
    id: Number(v.id),
    status,
    senderName: String(v.senderName ?? ''),
    amount: num(v.amount) ?? 0,
    reference: String(v.reference ?? ''),
    submittedAt: num(v.submittedAt) ?? 0,
    hasProof: !!v.hasProof,
    proofType: v.proofType ?? null,
    decidedAt: num(v.decidedAt),
    decidedByName: v.decidedByName ?? null,
    note: v.note ?? null,
    match: m ? {
      noticeId: Number(m.noticeId),
      senderName: m.senderName ?? null,
      amount: num(m.amount),
      interacRef: m.interacRef ?? null,
      receivedAt: num(m.receivedAt),
      matchedAt: num(m.matchedAt),
    } : null,
  };
}

/** The card's claim while dispatch has still to decide it. */
export const pendingClaim = (card: { paymentClaim?: PaymentClaim | null }): PaymentClaim | null =>
  card.paymentClaim && card.paymentClaim.status === 'pending' ? card.paymentClaim : null;

/** The stages a card with a pending claim may not move to (the database refuses them too — 0146's guard). */
export const CLAIM_LOCKED_STAGES: ReadonlySet<string> = new Set(['cancelled', 'unassigned', 'claimed', 'pending', 'processing']);

/** Why a card cannot be cancelled, archived or put back while its claim is pending — null when it can. */
export function claimLockText(card: { orderNumber: string; paymentClaim?: PaymentClaim | null }): string | null {
  return pendingClaim(card)
    ? `#${card.orderNumber}: the customer says they paid — confirm it (Payment Received) or reject it first.`
    : null;
}

/** The customer's amount differs from the order total (to the cent). */
export const claimAmountOff = (claim: Pick<PaymentClaim, 'amount'>, total: number): boolean =>
  Math.round((Number(claim.amount) || 0) * 100) !== Math.round((Number(total) || 0) * 100);

/** The matched notice's amount differs from what the customer said. */
export const matchAmountOff = (claim: PaymentClaim): boolean =>
  !!claim.match && claim.match.amount != null && claimAmountOff({ amount: claim.match.amount }, claim.amount);

/** The reference to put in the Payment Received dialog: the matched Interac reference, else what the customer typed. */
export const claimReference = (claim: PaymentClaim | null): string =>
  (claim?.match?.interacRef || claim?.reference || '').trim();

/** Same reference, ignoring case and punctuation. */
export const sameRef = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!refKey(a) && refKey(a) === refKey(b);

/** Dispatch rejects a pending claim. → the stage the card went back to. */
export async function rejectClaim(claimId: number, note: string): Promise<string> {
  const { data, error } = await supabase().rpc('payment_claim_reject', { p_claim: claimId, p_note: note.trim() || null });
  if (error) throw new Error(/permission|42501|Only dispatch/i.test(error.message) ? 'Only dispatch can reject a payment claim.' : error.message);
  if (!data?.ok) throw new Error(data?.reason === 'already_confirmed' ? 'That claim was already confirmed.' : 'That claim was already decided.');
  return String(data.stage || '');
}

/** A short signed link to the customer's payment confirmation (photo or PDF). */
export async function proofUrl(claimId: number): Promise<{ url: string; type: string | null }> {
  const res = await authedFetch(`/.netlify/functions/payment-claim-proof?claim=${encodeURIComponent(String(claimId))}`);
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body?.url) throw new Error(body?.error || `Could not open the confirmation (${res.status})`);
  return { url: String(body.url), type: body.type ?? null };
}
